import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import ErrorBoundary from './ErrorBoundary';
import { CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, MessageCircle, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { trackInitiateCheckout, trackViewContent } from '@/lib/analytics';
import { isProductUnavailable } from '@/lib/productAvailability';
import { parseProductDescription } from '@/lib/productDescription';
import { parseProductDetails } from '@/lib/productDetails';
import {
  getVariantInventoryTotal,
  getVariantSizeStock,
  getVariantTotalStock,
  hasVariantInventory,
} from '@/lib/productInventory';
import { getCurrentCustomerIdToken } from '@/lib/firebase';
import {
  buildProductLandingPath,
  findProductVariant,
  formatProductCategory,
} from '@/lib/productRoutes';
import { getProductPricing } from '@/lib/productPricing';

const DELIVERY_PROVIDER_CONFIGS = [
  {
    id: 'parcelgrid',
    url: '/data/parcelgrid-routes.json',
    fallbackName: 'ParcelGrid / Escrow Courier',
  },
  {
    id: 'pickup-mtaani',
    url: '/data/pickup-mtaani-routes.json',
    fallbackName: 'Pickup Mtaani',
  },
  {
    id: 'speedaf',
    url: '/data/speedaf-routes.json',
    fallbackName: 'Speedaf Express',
  },
];
const DEFAULT_DELIVERY_PROVIDER_ID = 'parcelgrid';
const DEFAULT_DELIVERY_PROVIDER = 'ParcelGrid / Escrow Courier';
const DEFAULT_PARCEL_COMMITMENT_FEE = 250;
const FREE_NAIROBI_CBD_ROUTE_ID = 'trendy-quest-nairobi-cbd-environs';
const PAYBILL_NUMBER = '522533';
const ACCOUNT_NUMBER = '7907601';
const WHATSAPP_NUMBER = '254717002340';
const PRODUCT_LANDING_TRUST_POINTS = [
  'Pay via M-Pesa',
  'Countrywide delivery',
  'Easy size exchange',
  'WhatsApp support available',
];
const EMAIL_VERIFICATION_INITIAL_STATE = {
  code: '',
  sentTo: '',
  expiresAt: 0,
  isCodeSent: false,
};
const TRENDY_QUEST_DELIVERY_PROVIDER = {
  id: 'trendy-quest-delivery',
  name: 'Trendy Quest Delivery',
  defaultFee: 0,
  selectionHint: 'Choose this for free delivery. Add your estate, building, or landmark for handoff.',
  source: 'Trendy Quest landing checkout',
  sourceCheckedAt: '',
  routes: [
    {
      id: FREE_NAIROBI_CBD_ROUTE_ID,
      town: 'Nairobi',
      county: 'Nairobi',
      businessName: 'Trendy Quest direct delivery',
      address: 'Free Trendy Quest direct delivery. Add your estate, building, or landmark.',
      label: 'Trendy Quest direct delivery - Free',
      fee: 0,
      note: 'Free delivery. Add your estate, building, or landmark for handoff.',
    },
  ],
};

const normalizeAmount = (value, fallback = 0) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue < 0) return fallback;
  return Math.round((numericValue + Number.EPSILON) * 100) / 100;
};

const formatCurrency = (value) => {
  const amount = normalizeAmount(value);
  const minimumFractionDigits = Number.isInteger(amount) ? 0 : 2;
  return `KES ${amount.toLocaleString('en-KE', {
    minimumFractionDigits,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeKenyaPhone = (value) => {
  let formatted = String(value || '').replace(/\D/g, '');
  if (formatted.startsWith('0')) formatted = `254${formatted.slice(1)}`;
  else if (formatted.startsWith('7') && formatted.length === 9) formatted = `254${formatted}`;
  else if (formatted.startsWith('2540')) formatted = `254${formatted.slice(4)}`;
  return formatted;
};

const buildDeliveryRouteLabel = (route) => {
  const explicitLabel = String(route?.label || '').trim();
  const town = String(route?.town || '').trim();
  const businessName = String(route?.businessName || '').trim();

  if (explicitLabel) return explicitLabel;
  if (!town) return businessName;
  return businessName ? `${town} - ${businessName}` : town;
};

const normalizeDeliveryRoute = (route, defaultFee) => {
  const id = String(route?.id || '').trim();
  const town = String(route?.town || route?.label || '').trim();

  if (!id || !town) return null;

  return {
    id,
    town,
    county: String(route?.county || '').trim(),
    businessName: String(route?.businessName || '').trim(),
    address: String(route?.address || '').trim(),
    label: buildDeliveryRouteLabel(route),
    fee: normalizeAmount(route?.fee, normalizeAmount(defaultFee, DEFAULT_PARCEL_COMMITMENT_FEE)),
    note: String(route?.note || '').trim(),
  };
};

const normalizeDeliveryProvider = (payload, config) => {
  const providerId = String(payload?.id || config?.id || '').trim();
  const providerName =
    String(payload?.provider || payload?.name || config?.fallbackName || '').trim() ||
    DEFAULT_DELIVERY_PROVIDER;

  if (!providerId || !providerName) return null;

  const defaultFee = normalizeAmount(payload?.commitmentFee, DEFAULT_PARCEL_COMMITMENT_FEE);
  const routes = (Array.isArray(payload?.routes) ? payload.routes : [])
    .map((route) => normalizeDeliveryRoute(route, defaultFee))
    .filter(Boolean);

  return {
    id: providerId,
    name: providerName,
    defaultFee,
    routes,
    selectionHint: String(payload?.selectionHint || '').trim(),
    source: String(payload?.source || '').trim(),
    sourceCheckedAt: String(payload?.sourceCheckedAt || '').trim(),
  };
};

const getOptionKey = (option, index) => String(option?.name || option?.image || `default-${index}`);

const normalizeSizeQuantityMap = (selection = {}, sizes = []) => {
  if (!sizes.length) return {};

  const allowedSizes = new Set(sizes.map((size) => String(size)));
  const sizeQuantities = {};

  if (selection?.sizeQuantities && typeof selection.sizeQuantities === 'object') {
    Object.entries(selection.sizeQuantities).forEach(([size, quantity]) => {
      const sizeKey = String(size);
      const normalizedQuantity = Math.max(0, Number(quantity) || 0);

      if (allowedSizes.has(sizeKey) && normalizedQuantity > 0) {
        sizeQuantities[sizeKey] = normalizedQuantity;
      }
    });
  }

  const legacySize = String(selection?.size || '').trim();
  const legacyQuantity = Math.max(0, Number(selection?.quantity) || 0);
  if (legacySize && allowedSizes.has(legacySize) && legacyQuantity > 0 && !sizeQuantities[legacySize]) {
    sizeQuantities[legacySize] = legacyQuantity;
  }

  return sizeQuantities;
};

const getSizeQuantityTotal = (sizeQuantities = {}) =>
  Object.values(sizeQuantities).reduce((sum, quantity) => sum + Math.max(0, Number(quantity) || 0), 0);

const getSizeQuantitySummary = (sizeQuantities = {}, sizes = []) =>
  sizes
    .map((size) => {
      const quantity = Math.max(0, Number(sizeQuantities[String(size)]) || 0);
      return quantity > 0 ? `${size} x${quantity}` : '';
    })
    .filter(Boolean)
    .join(', ');

const getSelectionStockError = (product, items = []) => {
  if (!product || !items.length) return '';

  if (hasVariantInventory(product)) {
    const overstockedItem = items.find((item) => {
      const availableStock = item.size
        ? getVariantSizeStock(product, item.color, item.size)
        : getVariantTotalStock(product, item.color);

      return availableStock !== null && item.quantity > availableStock;
    });

    if (overstockedItem) {
      const optionLabel = overstockedItem.color || 'Default';
      const sizeLabel = overstockedItem.size ? ` size ${overstockedItem.size}` : '';
      const availableStock = overstockedItem.size
        ? getVariantSizeStock(product, overstockedItem.color, overstockedItem.size)
        : getVariantTotalStock(product, overstockedItem.color);

      return `${optionLabel}${sizeLabel} only has ${availableStock} left.`;
    }

    return '';
  }

  const productStock = Math.max(0, Number(product?.stock) || 0);
  const totalSelected = items.reduce((sum, item) => sum + Math.max(0, Number(item.quantity) || 0), 0);

  return totalSelected > productStock ? `Only ${productStock} total left for this product.` : '';
};

const getSafeDomId = (value = '') =>
  String(value || 'default')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'default';

export default function ProductLandingCheckoutPage({
  product,
  recommendedProducts = [],
  selectedColorSlug = '',
}) {
  const router = useRouter();
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');
  const [lineSelections, setLineSelections] = useState({});
  const [expandedSizeTables, setExpandedSizeTables] = useState({});
  const colorCarouselRef = useRef(null);
  const [orderForm, setOrderForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    paymentChoice: 'paybill',
    paymentMessage: '',
    deliveryProviderId: '',
    deliveryRouteId: '',
  });
  const [deliveryConfig, setDeliveryConfig] = useState({
    providers: [],
    defaultProviderId: DEFAULT_DELIVERY_PROVIDER_ID,
  });
  const [routesLoading, setRoutesLoading] = useState(true);
  const [routesError, setRoutesError] = useState('');
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [emailVerification, setEmailVerification] = useState(EMAIL_VERIFICATION_INITIAL_STATE);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const initializedProductKeyRef = useRef('');
  const recommendedSectionRef = useRef(null);
  const hasAutoScrolledToRecommendationsRef = useRef(false);
  const { user, profile } = useCustomerAuth();

  useEffect(() => {
    if (!product) {
      initializedProductKeyRef.current = '';
      setIsSubmittingOrder(false);
      setEmailVerification(EMAIL_VERIFICATION_INITIAL_STATE);
      setHasTrackedView(false);
      setSelectedVariant(null);
      setSelectedImage('');
      setLineSelections({});
      setExpandedSizeTables({});
      return;
    }

    const productKey = String(product.id || product.name || '');
    const isNewProduct = initializedProductKeyRef.current !== productKey;
    initializedProductKeyRef.current = productKey;

    if (isNewProduct) {
      setIsSubmittingOrder(false);
      setEmailVerification(EMAIL_VERIFICATION_INITIAL_STATE);
      setHasTrackedView(false);
    }

    const pathname =
      typeof window !== 'undefined' ? window.location.pathname.replace(/\/$/, '') : '';
    const productPath = buildProductLandingPath(product);
    const routeColorPath = pathname.startsWith(`${productPath}/`)
      ? pathname.slice(productPath.length + 1)
      : '';
    const routeColorSlug = routeColorPath && !routeColorPath.includes('/') ? routeColorPath : '';
    const matchedVariant = findProductVariant(product, selectedColorSlug || routeColorSlug);
    const fallbackVariant = product?.variants?.[0] || null;
    const nextVariant = matchedVariant || fallbackVariant;

    setSelectedVariant(nextVariant);
    setSelectedImage(nextVariant?.image || product?.image || '');
    const options = product?.variants?.length ? product.variants : [{ name: '', image: product.image }];
    const productSizes = Array.isArray(product?.sizes) ? product.sizes : [];
    const defaultSelectionKey = getOptionKey(nextVariant || options[0], options.indexOf(nextVariant || options[0]));
    const shouldDefaultSelect = !isProductUnavailable(product);

    if (isNewProduct) {
      setExpandedSizeTables(productSizes.length && shouldDefaultSelect ? { [defaultSelectionKey]: true } : {});
    }

    setLineSelections((previous) => {
      const nextSelections = {};

      options.forEach((option, index) => {
        const key = getOptionKey(option, index);
        const previousSelection = !isNewProduct ? previous[key] : null;
        const previousSizeQuantities = normalizeSizeQuantityMap(previousSelection, productSizes);
        const sizeQuantities = previousSizeQuantities;

        nextSelections[key] = {
          quantity: productSizes.length
            ? getSizeQuantityTotal(sizeQuantities)
            : Math.max(
                0,
                Number(previousSelection?.quantity) ||
                  (isNewProduct && shouldDefaultSelect && key === defaultSelectionKey ? 1 : 0)
              ),
          size: productSizes.length ? '' : previousSelection?.size || '',
          sizeQuantities,
        };
      });

      return nextSelections;
    });
  }, [product, selectedColorSlug]);

  useEffect(() => {
    setOrderForm((previous) => ({
      ...previous,
      firstName: previous.firstName || profile?.firstName || '',
      lastName: previous.lastName || profile?.lastName || '',
      email: previous.email || user?.email || profile?.email || '',
    }));
  }, [profile, user]);

  useEffect(() => {
    let isActive = true;

    const loadRoutes = async () => {
      try {
        setRoutesLoading(true);
        setRoutesError('');

        const results = await Promise.allSettled(
          DELIVERY_PROVIDER_CONFIGS.map(async (config) => {
            const response = await fetch(config.url, { cache: 'no-store' });
            if (!response.ok) {
              throw new Error(`Could not load ${config.fallbackName} routes right now.`);
            }

            const payload = await response.json();
            return normalizeDeliveryProvider(payload, config);
          })
        );

        const providers = [
          TRENDY_QUEST_DELIVERY_PROVIDER,
          ...results
            .filter((result) => result.status === 'fulfilled' && result.value)
            .map((result) => result.value)
            .filter((provider) => provider.routes.length),
        ];

        if (!isActive) return;

        if (!providers.length) {
          throw new Error('No delivery options are available right now.');
        }

        const defaultProviderId =
          providers.find((provider) => provider.id === DEFAULT_DELIVERY_PROVIDER_ID)?.id ||
          providers[0]?.id ||
          DEFAULT_DELIVERY_PROVIDER_ID;

        setDeliveryConfig({
          providers,
          defaultProviderId,
        });

        if (results.some((result) => result.status === 'rejected')) {
          setRoutesError('Some delivery partners could not load. You can still continue with the available options.');
        }
      } catch (error) {
        if (!isActive) return;
        setRoutesError(error instanceof Error ? error.message : 'Could not load delivery routes right now.');
      } finally {
        if (isActive) setRoutesLoading(false);
      }
    };

    void loadRoutes();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (!deliveryConfig.providers.length) return;

    setOrderForm((previous) => {
      const providerExists = deliveryConfig.providers.some(
        (provider) => provider.id === previous.deliveryProviderId
      );
      const deliveryProviderId = providerExists ? previous.deliveryProviderId : '';
      const activeRoutes =
        deliveryConfig.providers.find((provider) => provider.id === deliveryProviderId)?.routes || [];
      const routeExists = activeRoutes.some((route) => route.id === previous.deliveryRouteId);
      const paymentChoice =
        ['parcelgrid', 'speedaf'].includes(deliveryProviderId) && previous.paymentChoice === 'pay_on_delivery'
          ? 'pay_on_delivery'
          : 'paybill';

      return {
        ...previous,
        deliveryProviderId,
        deliveryRouteId: routeExists ? previous.deliveryRouteId : '',
        paymentChoice,
      };
    });
  }, [deliveryConfig.defaultProviderId, deliveryConfig.providers]);

  useEffect(() => {
    if (!product || hasTrackedView) return;
    if (trackViewContent(product)) {
      setHasTrackedView(true);
    }
  }, [product, hasTrackedView]);

  const galleryImages = useMemo(
    () =>
      (product ? [product.image, ...(product.variants?.map((variant) => variant.image) || [])] : [])
        .filter(Boolean)
        .filter((image, index, images) => images.indexOf(image) === index),
    [product]
  );

  const currentImage = selectedImage || selectedVariant?.image || product?.image;
  const descriptionContent = useMemo(
    () => parseProductDescription(product?.description, product?.name),
    [product?.description, product?.name]
  );
  const productDetails = useMemo(() => parseProductDetails(product?.details), [product?.details]);
  const descriptionSummary = descriptionContent.summary;
  const { salePrice: resolvedSalePrice, regularPrice: resolvedOriginalPrice } = getProductPricing(product);
  const hasOriginalPrice = resolvedOriginalPrice > resolvedSalePrice;
  const isUnavailable = isProductUnavailable(product);
  const hasRecommendedProducts = recommendedProducts.length > 0;
  const shouldShowRecommendedProducts = isUnavailable && hasRecommendedProducts;
  const whatsappSupportHref = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hi Trendy Quest! I need help with ${product?.name || 'this product'}.`
  )}`;
  const orderOptions = useMemo(
    () => (product?.variants?.length ? product.variants : [{ name: '', image: product?.image || '' }]),
    [product]
  );
  const productSizes = useMemo(
    () => (Array.isArray(product?.sizes) ? product.sizes : []),
    [product?.sizes]
  );
  const selectedProvider = useMemo(
    () => deliveryConfig.providers.find((provider) => provider.id === orderForm.deliveryProviderId) || null,
    [deliveryConfig.providers, orderForm.deliveryProviderId]
  );
  const activeRoutes = useMemo(() => selectedProvider?.routes || [], [selectedProvider]);
  const selectedRoute = useMemo(
    () => activeRoutes.find((route) => route.id === orderForm.deliveryRouteId) || null,
    [activeRoutes, orderForm.deliveryRouteId]
  );
  const isFreeNairobiCbdDelivery = selectedRoute?.id === FREE_NAIROBI_CBD_ROUTE_ID;
  const isPickupMtaaniDelivery = selectedProvider?.id === 'pickup-mtaani';
  const canPayOnDelivery = ['parcelgrid', 'speedaf'].includes(selectedProvider?.id);
  const effectivePaymentChoice =
    isPickupMtaaniDelivery || !canPayOnDelivery ? 'paybill' : orderForm.paymentChoice;
  const selectedOrderItems = useMemo(
    () =>
      orderOptions
        .flatMap((option, index) => {
          const key = getOptionKey(option, index);
          const selection = lineSelections[key] || {};
          const baseItem = {
            productId: product?.id || '',
            name: product?.name || 'Product',
            price: resolvedSalePrice,
            originalPrice: resolvedOriginalPrice || resolvedSalePrice,
            discount: getProductPricing(product).discount,
            color: option?.name || '',
            image: option?.image || product?.image || '',
          };

          if (productSizes.length) {
            const sizeQuantities = normalizeSizeQuantityMap(selection, productSizes);

            return productSizes
              .map((size) => {
                const quantity = Math.max(0, Number(sizeQuantities[String(size)]) || 0);
                if (!quantity) return null;

                return {
                  ...baseItem,
                  id: `${product?.id || 'product'}-${key}-${size}`,
                  quantity,
                  size: String(size),
                };
              })
              .filter(Boolean);
          }

          const quantity = Math.max(0, Number(selection.quantity) || 0);
          if (!quantity) return [];

          return [
            {
              ...baseItem,
              id: `${product?.id || 'product'}-${key}`,
              quantity,
              size: '',
            },
          ];
        })
        .filter(Boolean),
    [lineSelections, orderOptions, product, productSizes, resolvedOriginalPrice, resolvedSalePrice]
  );
  const matchedOrderOptionIndex = orderOptions.findIndex(
    (option) =>
      option === selectedVariant ||
      (selectedVariant?.name && option?.name === selectedVariant.name) ||
      (selectedVariant?.image && option?.image === selectedVariant.image)
  );
  const activeOrderOptionIndex = matchedOrderOptionIndex >= 0 ? matchedOrderOptionIndex : 0;
  const activeOrderOption = orderOptions[activeOrderOptionIndex];
  const scrollColorCarousel = (direction) => {
    colorCarouselRef.current?.scrollBy({
      left: direction * Math.max(180, colorCarouselRef.current.clientWidth * 0.8),
      behavior: 'smooth',
    });
  };
  const selectedQuantity = selectedOrderItems.reduce((sum, item) => sum + item.quantity, 0);
  const selectionStockError = getSelectionStockError(product, selectedOrderItems);
  const detailedInventoryTotal = hasVariantInventory(product) ? getVariantInventoryTotal(product) : null;
  const landingTotals = useMemo(() => {
    const subtotal = normalizeAmount(
      selectedOrderItems.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0)
    );
    const commitmentFee = subtotal > 0 && selectedRoute ? normalizeAmount(selectedRoute.fee) : 0;
    const codBalance = subtotal;
    const total = normalizeAmount(codBalance + commitmentFee);

    return {
      subtotal,
      commitmentFee,
      codBalance,
      total,
    };
  }, [selectedOrderItems, selectedRoute]);
  const displayedCommitmentFee = landingTotals.commitmentFee;
  const displayedTotal = landingTotals.total;
  const updateOrderField = (name, value) => {
    setOrderForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    if (name === 'email') {
      setEmailVerification(EMAIL_VERIFICATION_INITIAL_STATE);
    }
  };

  const updateDeliveryProvider = (value) => {
    setOrderForm((previous) => ({
      ...previous,
      deliveryProviderId: value,
      deliveryRouteId: '',
      paymentChoice: ['parcelgrid', 'speedaf'].includes(value) ? previous.paymentChoice : 'paybill',
    }));
  };

  const updateLineSelection = (key, patch) => {
    setLineSelections((previous) => ({
      ...previous,
      [key]: {
        ...(previous[key] || {}),
        ...patch,
      },
    }));
  };

  const updateOptionQuantity = (key, option, quantityOrUpdater) => {
    setLineSelections((previous) => {
      const previousSelection = previous[key] || {};
      const currentQuantity = Math.max(0, Number(previousSelection.quantity) || 0);
      const rawNextQuantity =
        typeof quantityOrUpdater === 'function' ? quantityOrUpdater(currentQuantity) : quantityOrUpdater;
      const maxQuantity = hasVariantInventory(product)
        ? getVariantTotalStock(product, option)
        : Math.max(0, Number(product?.stock) || 0);
      const nextQuantity = Math.max(0, Number(rawNextQuantity) || 0);
      const cappedQuantity = maxQuantity === null ? nextQuantity : Math.min(nextQuantity, maxQuantity);

      return {
        ...previous,
        [key]: {
          ...previousSelection,
          quantity: cappedQuantity,
        },
      };
    });
  };

  const updateSizeQuantity = (key, option, size, quantityOrUpdater) => {
    const sizeKey = String(size);

    setLineSelections((previous) => {
      const previousSelection = previous[key] || {};
      const previousSizeQuantities = normalizeSizeQuantityMap(previousSelection, productSizes);
      const currentQuantity = Math.max(0, Number(previousSizeQuantities[sizeKey]) || 0);
      const rawNextQuantity =
        typeof quantityOrUpdater === 'function' ? quantityOrUpdater(currentQuantity) : quantityOrUpdater;
      const availableStock = getVariantSizeStock(product, option, sizeKey);
      const nextQuantity = Math.max(0, Number(rawNextQuantity) || 0);
      const cappedQuantity = availableStock === null ? nextQuantity : Math.min(nextQuantity, availableStock);
      const nextSizeQuantities = {
        ...previousSizeQuantities,
        [sizeKey]: cappedQuantity,
      };

      if (cappedQuantity === 0) {
        delete nextSizeQuantities[sizeKey];
      }

      return {
        ...previous,
        [key]: {
          ...previousSelection,
          quantity: getSizeQuantityTotal(nextSizeQuantities),
          size: '',
          sizeQuantities: nextSizeQuantities,
        },
      };
    });

    setExpandedSizeTables({ [key]: true });
  };

  const toggleSizeTable = (key, fallbackExpanded = false) => {
    setExpandedSizeTables((previous) => ({ [key]: !(previous[key] ?? fallbackExpanded) }));
  };

  const openSizeTable = (key) => {
    setExpandedSizeTables((previous) => (previous[key] && Object.keys(previous).length === 1 ? previous : { [key]: true }));
  };

  const updateVariantRoute = useCallback(
    async (variant) => {
      if (!product) return;

      const targetPath = buildProductLandingPath(product, { color: variant?.name });
      const currentPath = String(router.asPath || '').split('?')[0];

      if (!targetPath || targetPath === currentPath) return;

      try {
        await router.replace(targetPath, undefined, { scroll: false, shallow: true });
      } catch {
        // Keep the selection change even if route replacement fails.
      }
    },
    [product, router]
  );

  const selectDisplayImage = (image) => {
    setSelectedImage(image || product?.image || '');
  };

  const scrollToRecommendedProducts = useCallback(() => {
    if (!recommendedSectionRef.current) return;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    recommendedSectionRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  const selectOrderVariant = (option, { syncRoute = false } = {}) => {
    setSelectedVariant(option);
    setSelectedImage(option?.image || product?.image || '');

    if (syncRoute) {
      void updateVariantRoute(option);
    }
  };

  useEffect(() => {
    if (!shouldShowRecommendedProducts || hasAutoScrolledToRecommendationsRef.current) return;

    hasAutoScrolledToRecommendationsRef.current = true;
    const scrollTimer = window.setTimeout(scrollToRecommendedProducts, 900);

    return () => {
      window.clearTimeout(scrollTimer);
    };
  }, [scrollToRecommendedProducts, shouldShowRecommendedProducts]);

  const requestEmailVerificationCode = async (email) => {
    setVerificationLoading(true);

    try {
      const response = await fetch('/api/order-email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      const payload = await response.json();

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Could not send your verification code.');
      }

      setEmailVerification({
        code: '',
        sentTo: email.trim().toLowerCase(),
        expiresAt: Number(payload.expiresAt) || 0,
        isCodeSent: true,
      });
      toast.success('We sent a 6-digit code to your email.');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleLandingOrderSubmit = async (event) => {
    event.preventDefault();

    if (typeof event.currentTarget.reportValidity === 'function' && !event.currentTarget.reportValidity()) {
      return;
    }

    if (isUnavailable) {
      toast.error('This product is currently out of stock.');
      return;
    }

    if (!selectedOrderItems.length) {
      toast.error('Select at least one color quantity before placing the order.');
      return;
    }

    if (selectionStockError) {
      toast.error(selectionStockError);
      return;
    }

    if (productSizes.length && selectedOrderItems.some((item) => !item.size)) {
      toast.error('Select a size for every color you are ordering.');
      return;
    }

    if (!selectedProvider || !selectedRoute) {
      toast.error('Please choose your delivery partner and pickup point or service area.');
      return;
    }

    if (orderForm.paymentChoice === 'pay_on_delivery' && !canPayOnDelivery) {
      toast.error('Pay on delivery is only available for ParcelGrid and Speedaf delivery.');
      return;
    }

    const firstName = orderForm.firstName.trim();
    const lastName = orderForm.lastName.trim();
    const email = orderForm.email.trim();
    const address = orderForm.address.trim();
    const paymentMessage = orderForm.paymentMessage.trim();
    const normalizedPhone = normalizeKenyaPhone(orderForm.phone);

    if (!firstName || !lastName || !email || (isFreeNairobiCbdDelivery && !address)) {
      toast.error('Please complete your personal and delivery details before continuing.');
      return;
    }

    if (!/^254\d{9}$/.test(normalizedPhone)) {
      toast.error('Enter a valid Kenyan phone number in the format 2547XXXXXXXX.');
      return;
    }

    if (isPickupMtaaniDelivery && !paymentMessage) {
      toast.error('Pickup Mtaani requires Paybill payment first. Paste your M-Pesa payment message to continue.');
      return;
    }

    const verificationEmail = email.toLowerCase();
    const verificationCode = emailVerification.code.trim();
    const hasCurrentVerificationCode =
      emailVerification.isCodeSent && emailVerification.sentTo === verificationEmail;

    if (!hasCurrentVerificationCode) {
      try {
        await requestEmailVerificationCode(email);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Could not send your verification code.');
      }
      return;
    }

    if (!/^\d{6}$/.test(verificationCode)) {
      toast.error('Enter the 6-digit verification code sent to your email.');
      return;
    }

    setIsSubmittingOrder(true);

    try {
      trackInitiateCheckout(selectedOrderItems);

      const customerAddress = isFreeNairobiCbdDelivery
        ? address
        : address || selectedRoute.label || selectedProvider.name;
      const customer = {
        firstName,
        lastName,
        name: `${firstName} ${lastName}`.trim(),
        email,
        phone: normalizedPhone,
        address: customerAddress,
      };
      const delivery = {
        providerId: selectedProvider.id,
        provider: selectedProvider.name,
        routeId: selectedRoute.id,
        routeLabel: selectedRoute.label,
        county: selectedRoute.county,
        businessName: selectedRoute.businessName,
        routeAddress: selectedRoute.address,
        routeFee: selectedRoute.fee,
        customerAddress,
      };
      const pricing = {
        subtotal: landingTotals.subtotal,
        tax: 0,
        vatApplied: false,
        commitmentFee: landingTotals.commitmentFee,
        codBalance: landingTotals.codBalance,
        total: landingTotals.total,
        currency: 'KES',
      };
      const idToken = await getCurrentCustomerIdToken().catch(() => '');
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({
          customer,
          items: selectedOrderItems,
          total: pricing.total,
          paymentMethod: 'resend',
          status: 'awaiting_confirmation',
          payment: {
            provider: 'resend',
            status:
              effectivePaymentChoice === 'pay_on_delivery'
                ? 'pay_on_delivery_selected'
                : 'paybill_confirmation_pending',
            method: effectivePaymentChoice,
            paymentMessage,
            updatedAt: Date.now(),
          },
          delivery,
          pricing,
          emailVerificationCode: verificationCode,
          source: 'product_landing_checkout',
        }),
      });
      const payload = await response.json();

      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Could not prepare your order right now.');
      }

      const orderId = payload.orderId;
      const orderNumber = payload.orderNumber || payload.orderId;

      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(
          'lastOrder',
          JSON.stringify({
            orderId,
            orderNumber,
            paymentMethod: 'resend',
            emailSent: Boolean(payload.emailSent),
            customerEmailSent: Boolean(payload.customerEmailSent),
          })
        );
      }

      if (payload.emailSent === false) {
        toast.error('Order saved, but email notification needs admin follow-up.');
      } else {
        toast.success('Order sent. We will confirm it by email or phone.');
      }

      await router.push('/thank-you');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not prepare your order right now.');
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  if (router.isFallback || !product) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  const resolvedVariant = findProductVariant(product, selectedColorSlug);
  const resolvedColorName = String(resolvedVariant?.name || '').trim();
  const { salePrice, discount } = getProductPricing(product);
  const displayPrice = formatCurrency(salePrice);
  const ogTitle = `${product.name}${resolvedColorName ? ` - ${resolvedColorName}` : ''} | Trendy Quest`;
  const ogDescription = `${displayPrice} | Free delivery in Nairobi CBD, countrywide delivery available`;

  return (
    <>
      <ErrorBoundary>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="min-h-screen overflow-x-hidden bg-[#fbfaf5] py-0 sm:py-6 lg:py-8"
      >
      <div className="w-full px-0 sm:px-4 md:px-6">
        <div className="mx-auto w-full max-w-[1180px] space-y-3 sm:space-y-6">
          <header className="flex justify-center">
            <div className="flex w-full max-w-xl items-center gap-3 border border-x-0 border-[#e7dfcf] bg-white px-3 py-3 shadow-[0_12px_32px_rgba(21,35,24,0.06)] min-[430px]:rounded-2xl min-[430px]:border-x sm:px-4 lg:max-w-none lg:justify-center">
              <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-[#fffdf7] sm:h-12 sm:w-12">
                <Image
                  src="/logo-yatiky.png"
                  alt="Trendy Quest"
                  fill
                  sizes="48px"
                  className="object-contain"
                  priority
                />
              </div>
              <div className="min-w-0">
                <p className="font-playfair text-lg font-bold text-[#184d26] sm:text-xl">Trendy Quest</p>
                <p className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-[#667063] sm:text-xs sm:tracking-[0.18em]">
                  {product.name}
                </p>
              </div>
            </div>
          </header>

          <div className="grid min-w-0 items-start gap-3 sm:gap-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(440px,1fr)] lg:gap-7">
            <div className="min-w-0 space-y-2 sm:space-y-3 lg:sticky lg:top-5 lg:space-y-4">
              <div className="overflow-hidden border border-x-0 border-[#e7dfcf] bg-white shadow-[0_16px_45px_rgba(21,35,24,0.06)] min-[430px]:rounded-2xl min-[430px]:border-x">
                <div className="relative aspect-square w-full overflow-hidden bg-[#f5f2e8]">
                  <Image
                    src={currentImage}
                    alt={product.name}
                    fill
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 100vw, 52vw"
                    className="object-cover object-center"
                  />
                  {isUnavailable ? (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/35 to-transparent p-4 sm:p-5">
                      <span className="inline-flex rounded-full bg-white px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-[#182218] shadow-sm">
                        Out of stock
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {galleryImages.length > 1 ? (
                <div className="grid grid-cols-4 gap-2 border border-x-0 border-[#e7dfcf] bg-white p-2 shadow-sm min-[390px]:grid-cols-5 min-[430px]:rounded-2xl min-[430px]:border-x sm:gap-3 sm:p-3 lg:grid-cols-4 xl:grid-cols-5">
                  {galleryImages.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      onClick={() => {
                        selectDisplayImage(image);
                      }}
                      className={`relative aspect-square overflow-hidden rounded-xl border-2 transition ${
                        currentImage === image ? 'border-[#24552d]' : 'border-[#e7dfcf]'
                      }`}
                      aria-label={`Preview ${product.name} image ${index + 1}`}
                    >
                      <Image
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        fill
                        sizes="140px"
                        className="object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="min-w-0 space-y-4 border border-x-0 border-[#e7dfcf] bg-white p-3 shadow-[0_16px_45px_rgba(21,35,24,0.06)] min-[390px]:p-4 min-[430px]:rounded-2xl min-[430px]:border-x sm:space-y-5 sm:p-6">
              <div className="min-w-0">
                <h1 className="text-2xl font-playfair font-bold leading-tight text-[#182218] sm:text-4xl">
                  {product.name}
                </h1>
                {descriptionSummary ? (
                  <p className="mt-4 text-base leading-7 text-[#62675f] sm:text-lg">
                    {descriptionSummary}
                  </p>
                ) : null}
                {productDetails.length ? (
                  <section className="mt-4 rounded-xl border border-[#efe7d6] bg-[#fcfaf4] p-3 min-[390px]:p-4">
                    <h2 className="text-sm font-extrabold uppercase tracking-[0.14em] text-[#6d604d]">Specs</h2>
                    <div className="mt-3 grid gap-2">
                      {productDetails.map((detail) => (
                        <div
                          key={detail.key}
                          className={`min-w-0 gap-1 border-b border-[#ede4d1] pb-2 last:border-b-0 last:pb-0 min-[430px]:gap-3 ${
                            detail.label ? 'grid min-[430px]:grid-cols-[minmax(96px,0.38fr)_minmax(0,1fr)]' : 'block'
                          }`}
                        >
                          {detail.label ? (
                            <span className="text-sm font-semibold text-[#667063]">{detail.label}</span>
                          ) : null}
                          <span className="text-sm leading-6 text-[#182218]">{detail.value}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                ) : null}
              </div>

              <div className="rounded-xl border border-[#efd7a3] bg-[#fff8ec] p-3 min-[390px]:p-4">
                <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.14em] text-[#6d604d]">Price</p>
                <div className="flex flex-wrap items-end gap-3">
                  <p className="text-2xl font-black leading-none text-[#0a0a0a] sm:text-4xl">
                    KES {resolvedSalePrice}
                  </p>
                  {hasOriginalPrice ? (
                    <p className="pb-1 text-base text-[#8d9386] line-through">KES {resolvedOriginalPrice}</p>
                  ) : null}
                  {hasOriginalPrice ? (
                    <span className="rounded-full bg-[#24552d] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white">
                      -{discount}% off
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 text-sm text-[#667063]">{formatProductCategory(product)}</p>
              </div>

              <section className="rounded-xl border-2 border-[#1f7a3f] bg-[#ecfff2] p-4 shadow-[0_18px_45px_rgba(31,122,63,0.14)] min-[430px]:p-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  {PRODUCT_LANDING_TRUST_POINTS.map((point) => (
                    <div key={point} className="flex items-center gap-2 text-sm font-extrabold text-[#12351f] sm:text-base">
                      <CheckCircle2 size={20} className="shrink-0 text-[#1f7a3f]" aria-hidden="true" />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
                <a
                  href={whatsappSupportHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-[#1f7a3f] px-5 text-sm font-extrabold text-white shadow-[0_12px_28px_rgba(31,122,63,0.26)] transition hover:bg-[#176030] sm:text-base"
                >
                  <MessageCircle size={20} aria-hidden="true" />
                  WhatsApp +254 717 002 340
                </a>
              </section>

              <form onSubmit={handleLandingOrderSubmit} className="space-y-5">
                {isUnavailable ? (
                  <section className="rounded-xl border border-[#efd7a3] bg-[#fff8ec] p-4 min-[430px]:p-5">
                    <h2 className="text-lg font-semibold text-[#182218]">This pair is currently out of stock</h2>
                    <p className="mt-2 text-sm leading-6 text-[#667063]">
                      The shoe photo is still shown for reference. Browse similar available pairs below.
                    </p>
                    {shouldShowRecommendedProducts ? (
                      <button
                        type="button"
                        onClick={scrollToRecommendedProducts}
                        className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-xl bg-[#173822] px-4 text-sm font-semibold text-white transition hover:bg-[#24552d]"
                      >
                        View similar shoes
                      </button>
                    ) : null}
                  </section>
                ) : null}
                <section className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-[#182218]">Choose color, size and quantity</h2>
                    <span className="text-sm text-[#667063]">{selectedQuantity} selected</span>
                  </div>
                  {detailedInventoryTotal !== null ? (
                    <p className="text-sm text-[#667063]">{detailedInventoryTotal} total left across available colors and sizes.</p>
                  ) : null}
                  {selectionStockError ? (
                    <p className="rounded-lg border border-[#f2c6c6] bg-[#fff7f7] px-3 py-2 text-sm font-semibold text-[#b42318]">
                      {selectionStockError}
                    </p>
                  ) : null}
                  <div className="relative">
                    {orderOptions.length > 1 ? (
                      <>
                        <button
                          type="button"
                          onClick={() => scrollColorCarousel(-1)}
                          className="absolute left-2 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#e6dcc8] bg-white/95 text-[#24552d] shadow-sm transition hover:bg-[#f2ead8] sm:flex"
                          aria-label="Scroll to previous color"
                        >
                          <ChevronLeft size={18} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => scrollColorCarousel(1)}
                          className="absolute right-2 top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-[#e6dcc8] bg-white/95 text-[#24552d] shadow-sm transition hover:bg-[#f2ead8] sm:flex"
                          aria-label="Scroll to next color"
                        >
                          <ChevronRight size={18} aria-hidden="true" />
                        </button>
                      </>
                    ) : null}
                    <div
                      ref={colorCarouselRef}
                      className="flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth pb-3 touch-pan-x [scrollbar-width:thin]"
                      aria-label="Choose a product color"
                    >
                      {orderOptions.map((option, index) => {
                        const key = getOptionKey(option, index);
                        const selection = lineSelections[key] || {};
                        const sizeQuantities = normalizeSizeQuantityMap(selection, productSizes);
                        const quantity = productSizes.length
                          ? getSizeQuantityTotal(sizeQuantities)
                          : Math.max(0, Number(selection.quantity) || 0);
                        const optionStock = hasVariantInventory(product)
                          ? getVariantTotalStock(product, option)
                          : null;
                        const isOptionOutOfStock = optionStock !== null && optionStock <= 0;
                        const isActive = option === activeOrderOption;

                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              selectOrderVariant(option, { syncRoute: true });
                              if (productSizes.length) openSizeTable(key);
                            }}
                            aria-pressed={isActive}
                            className={`w-[150px] shrink-0 snap-start overflow-hidden rounded-xl border text-left transition min-[430px]:w-[172px] ${
                              isActive
                                ? 'border-[#24552d] bg-[#f2faf3] ring-1 ring-[#24552d]'
                                : 'border-[#e6dcc8] bg-white hover:border-[#8eaa91]'
                            }`}
                          >
                            <span className="relative block aspect-[4/3] w-full bg-[#f5f2e8]">
                              <Image
                                src={option.image || product.image}
                                alt={`${option.name || product.name} color`}
                                fill
                                sizes="172px"
                                className="object-cover"
                              />
                            </span>
                            <span className="block p-3">
                              <span className="block truncate font-semibold text-[#182218]">
                                {option.name || 'Default'}
                              </span>
                              <span className="mt-1 block text-xs text-[#667063]">
                                {quantity} selected
                                {optionStock !== null
                                  ? ` | ${isOptionOutOfStock ? 'Out of stock' : `${optionStock} left`}`
                                  : ''}
                              </span>
                              {isActive ? (
                                <span className="mt-2 block text-xs font-bold text-[#24552d]">Selected color</span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <p className="text-xs text-[#667063]">
                    Swipe or tap a color above, then manually select sizes and quantities below.
                  </p>
                  <div className="grid gap-3">
                    {[activeOrderOption].filter(Boolean).map((option) => {
                      const index = activeOrderOptionIndex;
                      const key = getOptionKey(option, index);
                      const selection = lineSelections[key] || {};
                      const sizeQuantities = normalizeSizeQuantityMap(selection, productSizes);
                      const hasSizes = productSizes.length > 0;
                      const quantity = hasSizes
                        ? getSizeQuantityTotal(sizeQuantities)
                        : Math.max(0, Number(selection.quantity) || 0);
                      const isSelectedOption = quantity > 0;
                      const sizeSummary = getSizeQuantitySummary(sizeQuantities, productSizes);
                      const hasExplicitSizeTableState = Object.keys(expandedSizeTables).length > 0;
                      const isSizeTableExpanded =
                        hasSizes && (expandedSizeTables[key] ?? (!hasExplicitSizeTableState && isSelectedOption));
                      const sizeTableId = `size-table-${getSafeDomId(key)}`;
                      const optionStock = hasVariantInventory(product)
                        ? getVariantTotalStock(product, option)
                        : null;
                      const isOptionOutOfStock = optionStock !== null && optionStock <= 0;

                      return (
                        <div
                          key={key}
                          onClick={() => {
                            selectOrderVariant(option, { syncRoute: true });
                            if (hasSizes) openSizeTable(key);
                          }}
                          className={`rounded-xl border p-3 ${
                            isSelectedOption
                              ? 'border-[#24552d] bg-[#f7fbf7]'
                              : 'border-[#e6dcc8] bg-white'
                          } cursor-pointer transition hover:border-[#24552d]`}
                        >
                          <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 min-[430px]:grid-cols-[auto_minmax(0,1fr)_auto]">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                selectOrderVariant(option, { syncRoute: true });
                                if (hasSizes) openSizeTable(key);
                              }}
                              className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#f5f2e8]"
                              aria-label={`View ${option.name || product.name}`}
                            >
                              <Image
                                src={option.image || product.image}
                                alt={option.name || product.name}
                                fill
                                sizes="64px"
                                className="object-cover"
                              />
                            </button>
                            <div className="min-w-0">
                              <p className="font-semibold text-[#182218]">{option.name || 'Default'}</p>
                              <p className="text-sm text-[#667063]">
                                {formatCurrency(resolvedSalePrice)} each
                                {optionStock !== null
                                  ? ` | ${isOptionOutOfStock ? 'Out of stock' : `${optionStock} left`}`
                                  : ''}
                              </p>
                            </div>
                            {hasSizes ? (
                              <div className="col-span-2 flex w-full flex-wrap items-center justify-between gap-2 min-[430px]:col-span-1 min-[430px]:justify-end">
                                <span className="rounded-full border border-[#d9eadc] bg-[#f2faf3] px-3 py-1 text-xs font-semibold text-[#24552d]">
                                  {quantity} selected
                                </span>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    selectOrderVariant(option, { syncRoute: true });
                                    toggleSizeTable(key, isSizeTableExpanded);
                                  }}
                                  className="inline-flex min-h-8 items-center gap-1 rounded-full border border-[#e6dcc8] bg-white px-3 text-xs font-semibold text-[#182218] transition hover:border-[#24552d]"
                                  aria-expanded={isSizeTableExpanded}
                                  aria-controls={sizeTableId}
                                >
                                  {isSizeTableExpanded ? 'Hide sizes' : 'Show sizes'}
                                  <ChevronDown
                                    size={14}
                                    className={`transition-transform ${isSizeTableExpanded ? 'rotate-180' : ''}`}
                                    aria-hidden="true"
                                  />
                                </button>
                              </div>
                            ) : (
                              <div className="col-span-2 flex w-full items-center justify-between gap-2 rounded-xl border border-[#e6dcc8] bg-[#fffdfa] p-1 min-[430px]:col-span-1 min-[430px]:w-auto min-[430px]:justify-start">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    selectOrderVariant(option);
                                    updateOptionQuantity(key, option, (currentQuantity) => currentQuantity - 1);
                                  }}
                                  disabled={quantity <= 0 || isOptionOutOfStock}
                                  className="flex h-9 w-9 items-center justify-center rounded-lg text-lg font-bold text-[#24552d] transition hover:bg-[#f2ead8] disabled:cursor-not-allowed disabled:text-[#b8b2a6] disabled:hover:bg-transparent"
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  max={optionStock ?? undefined}
                                  value={quantity}
                                  disabled={isOptionOutOfStock}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    selectOrderVariant(option);
                                  }}
                                  onFocus={() => selectOrderVariant(option)}
                                  onChange={(event) => {
                                    selectOrderVariant(option);
                                    updateOptionQuantity(key, option, event.target.value);
                                  }}
                                  className="h-9 w-14 rounded-lg border border-[#e6dcc8] bg-white text-center text-sm font-bold text-[#182218] disabled:cursor-not-allowed disabled:bg-[#f1eee7] disabled:text-[#9b9385]"
                                  aria-label={`${option.name || 'Default'} quantity`}
                                />
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    selectOrderVariant(option);
                                    updateOptionQuantity(key, option, (currentQuantity) => currentQuantity + 1);
                                  }}
                                  disabled={isOptionOutOfStock || (optionStock !== null && quantity >= optionStock)}
                                  className="flex h-9 w-9 items-center justify-center rounded-lg text-lg font-bold text-[#24552d] transition hover:bg-[#f2ead8] disabled:cursor-not-allowed disabled:text-[#b8b2a6] disabled:hover:bg-transparent"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                          {hasSizes ? (
                            <div
                              id={sizeTableId}
                              className={`mt-3 rounded-xl border border-[#e6dcc8] bg-[#fffdfa] ${
                                isSizeTableExpanded ? 'p-2' : 'p-3'
                              }`}
                            >
                              {isSizeTableExpanded ? (
                                <>
                                  <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-1 pb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#667063]">
                                    <span>Size</span>
                                    <span>Qty</span>
                                  </div>
                                  <div className="grid gap-2">
                                    {productSizes.map((optionSize) => {
                                      const sizeKey = String(optionSize);
                                      const sizeQuantity = Math.max(0, Number(sizeQuantities[sizeKey]) || 0);
                                      const sizeStock = getVariantSizeStock(product, option, sizeKey);
                                      const isSizeOutOfStock = sizeStock !== null && sizeStock <= 0;

                                      return (
                                        <div
                                          key={sizeKey}
                                          className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-lg border px-3 py-2 ${
                                            isSizeOutOfStock
                                              ? 'border-[#ead6d6] bg-[#fff7f7]'
                                              : sizeQuantity > 0
                                              ? 'border-[#24552d] bg-[#f7fbf7]'
                                              : 'border-[#efe7d6] bg-white'
                                          }`}
                                        >
                                          <span className="font-semibold text-[#182218]">
                                            {optionSize}
                                            {sizeStock !== null ? (
                                              <span className={`ml-2 text-xs ${isSizeOutOfStock ? 'text-[#b42318]' : 'text-[#667063]'}`}>
                                                {isSizeOutOfStock ? 'Out' : `${sizeStock} left`}
                                              </span>
                                            ) : null}
                                          </span>
                                          <div className="flex items-center gap-1 rounded-lg border border-[#e6dcc8] bg-[#fffdfa] p-1">
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                selectOrderVariant(option);
                                                updateSizeQuantity(key, option, sizeKey, (currentQuantity) => currentQuantity - 1);
                                              }}
                                              disabled={sizeQuantity <= 0 || isSizeOutOfStock}
                                              className="flex h-8 w-8 items-center justify-center rounded-md text-base font-bold text-[#24552d] transition hover:bg-[#f2ead8] disabled:cursor-not-allowed disabled:text-[#b8b2a6] disabled:hover:bg-transparent"
                                              aria-label={`Decrease ${option.name || 'Default'} size ${optionSize} quantity`}
                                            >
                                              -
                                            </button>
                                            <input
                                              type="number"
                                              min="0"
                                              max={sizeStock ?? undefined}
                                              value={sizeQuantity}
                                              disabled={isSizeOutOfStock}
                                              onClick={(event) => event.stopPropagation()}
                                              onFocus={() => selectOrderVariant(option)}
                                              onChange={(event) => {
                                                event.stopPropagation();
                                                selectOrderVariant(option);
                                                updateSizeQuantity(key, option, sizeKey, event.target.value);
                                              }}
                                              className="h-8 w-12 rounded-md border border-[#e6dcc8] bg-white text-center text-sm font-bold text-[#182218] disabled:cursor-not-allowed disabled:bg-[#f1eee7] disabled:text-[#9b9385]"
                                              aria-label={`${option.name || 'Default'} size ${optionSize} quantity`}
                                            />
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                selectOrderVariant(option);
                                                updateSizeQuantity(key, option, sizeKey, (currentQuantity) => currentQuantity + 1);
                                              }}
                                              disabled={isSizeOutOfStock || (sizeStock !== null && sizeQuantity >= sizeStock)}
                                              className="flex h-8 w-8 items-center justify-center rounded-md text-base font-bold text-[#24552d] transition hover:bg-[#f2ead8] disabled:cursor-not-allowed disabled:text-[#b8b2a6] disabled:hover:bg-transparent"
                                              aria-label={`Increase ${option.name || 'Default'} size ${optionSize} quantity`}
                                            >
                                              +
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </>
                              ) : (
                                <p className="text-sm leading-6 text-[#667063]">
                                  {sizeSummary || 'No sizes selected'}
                                </p>
                              )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="rounded-xl border border-[#efe7d6] bg-[#fcfaf4] p-4 min-[430px]:p-5">
                  <h2 className="text-lg font-semibold text-[#182218]">Personal information</h2>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <input
                      value={orderForm.firstName}
                      onChange={(event) => updateOrderField('firstName', event.target.value)}
                      placeholder="First name *"
                      required
                      className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    />
                    <input
                      value={orderForm.lastName}
                      onChange={(event) => updateOrderField('lastName', event.target.value)}
                      placeholder="Last name *"
                      required
                      className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="mt-3 grid gap-3">
                    <input
                      type="email"
                      value={orderForm.email}
                      onChange={(event) => updateOrderField('email', event.target.value)}
                      placeholder="Email address *"
                      required
                      className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    />
                    <input
                      value={orderForm.phone}
                      onChange={(event) => updateOrderField('phone', event.target.value)}
                      placeholder="Phone number (2547XXXXXXXX) *"
                      required
                      className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="mt-4 rounded-xl border border-[#d9eadc] bg-[#f2faf3] p-3 min-[390px]:p-4">
                    <label className="text-sm font-semibold text-[#182218]" htmlFor="landing-email-code">
                      Email verification code
                    </label>
                    <div className="mt-2 grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <input
                        id="landing-email-code"
                        inputMode="numeric"
                        maxLength={6}
                        value={emailVerification.code}
                        onChange={(event) =>
                          setEmailVerification((previous) => ({
                            ...previous,
                            code: event.target.value.replace(/\D/g, '').slice(0, 6),
                          }))
                        }
                        placeholder="6-digit code"
                        disabled={!emailVerification.isCodeSent}
                        className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm disabled:bg-[#f4f1e8]"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const email = orderForm.email.trim();
                          if (!email) {
                            toast.error('Enter your email before requesting a code.');
                            return;
                          }
                          requestEmailVerificationCode(email).catch((error) => {
                            toast.error(
                              error instanceof Error ? error.message : 'Could not send your verification code.'
                            );
                          });
                        }}
                        disabled={verificationLoading || isSubmittingOrder}
                        className="rounded-xl border border-[#c7dbc9] bg-white px-4 py-2 text-sm font-semibold text-[#24552d] transition hover:border-[#24552d] disabled:cursor-not-allowed disabled:bg-[#eee9dd] disabled:text-[#8d9386]"
                      >
                        {verificationLoading ? 'Sending...' : emailVerification.isCodeSent ? 'Resend code' : 'Send code'}
                      </button>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-[#667063]">
                      {emailVerification.isCodeSent
                        ? `Enter the code sent to ${emailVerification.sentTo}. It expires in 10 minutes.`
                        : 'When you click Place order, we will email a 6-digit code before creating the order.'}
                    </p>
                  </div>
                </section>

                <section className="rounded-xl border border-[#efe7d6] bg-[#fcfaf4] p-4 min-[430px]:p-5">
                  <h2 className="text-lg font-semibold text-[#182218]">Delivery details</h2>
                  <div className="mt-4 grid gap-3">
                    <select
                      value={orderForm.deliveryProviderId}
                      onChange={(event) => updateDeliveryProvider(event.target.value)}
                      disabled={routesLoading || !deliveryConfig.providers.length}
                      required
                      className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Select delivery partner *</option>
                      {deliveryConfig.providers.map((provider) => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                    <select
                      value={orderForm.deliveryRouteId}
                      onChange={(event) => updateOrderField('deliveryRouteId', event.target.value)}
                      disabled={routesLoading || !selectedProvider || !activeRoutes.length}
                      required
                      className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    >
                      <option value="">Select pickup point / area *</option>
                      {activeRoutes.map((route) => (
                        <option key={route.id} value={route.id}>
                          {route.label}
                          {route.county ? ` (${route.county})` : ''}
                        </option>
                      ))}
                    </select>
                    {routesError ? <p className="text-sm text-red-600">{routesError}</p> : null}
                    {isFreeNairobiCbdDelivery ? (
                      <input
                        value={orderForm.address}
                        onChange={(event) => updateOrderField('address', event.target.value)}
                        placeholder="Estate, building, or landmark *"
                        required
                        className="rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                      />
                    ) : null}
                    {selectedRoute?.address ? (
                      <p className="mt-2 text-sm text-[#667063]">
                        <span className="font-semibold text-[#182218]">Pickup address:</span>{' '}
                        {selectedRoute.address}
                      </p>
                    ) : null}
                  </div>
                </section>

                <section className="rounded-xl border border-[#efe7d6] bg-[#fcfaf4] p-4 min-[430px]:p-5">
                  <h2 className="text-lg font-semibold text-[#182218]">Payment gate</h2>
                  <p className="mt-1 text-sm leading-6 text-[#667063]">
                    Choose payment after delivery details. Pickup Mtaani requires Paybill payment before placing an order.
                  </p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label
                      className={`rounded-xl border p-4 ${
                        effectivePaymentChoice === 'paybill'
                          ? 'border-[#24552d] bg-[#eef7ef]'
                          : 'border-[#e6dcc8] bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentChoice"
                        value="paybill"
                        checked={effectivePaymentChoice === 'paybill'}
                        onChange={() => updateOrderField('paymentChoice', 'paybill')}
                        className="mr-2"
                      />
                      <span className="font-semibold text-[#182218]">Pay via Paybill</span>
                      <span className="mt-2 block text-sm text-[#667063]">
                        Paybill {PAYBILL_NUMBER}, account {ACCOUNT_NUMBER}.
                      </span>
                    </label>
                    <label
                      className={`rounded-xl border p-4 ${
                        effectivePaymentChoice === 'pay_on_delivery'
                          ? 'border-[#24552d] bg-[#eef7ef]'
                          : !canPayOnDelivery
                            ? 'border-[#e5e7eb] bg-[#f3f4f6] text-[#9ca3af]'
                            : 'border-[#e6dcc8] bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentChoice"
                        value="pay_on_delivery"
                        checked={effectivePaymentChoice === 'pay_on_delivery'}
                        onChange={() => updateOrderField('paymentChoice', 'pay_on_delivery')}
                        disabled={!canPayOnDelivery}
                        className="mr-2"
                      />
                      <span className="font-semibold">Pay on delivery</span>
                      <span className="mt-2 block text-sm">
                        Available for ParcelGrid and Speedaf delivery only.
                      </span>
                    </label>
                  </div>
                  {effectivePaymentChoice === 'paybill' ? (
                    <textarea
                      value={orderForm.paymentMessage}
                      onChange={(event) => updateOrderField('paymentMessage', event.target.value)}
                      placeholder="Paste your M-Pesa payment message here"
                      required={isPickupMtaaniDelivery}
                      className="mt-4 min-h-24 w-full rounded-xl border border-[#e6dcc8] bg-white px-3 py-2 text-sm"
                    />
                  ) : null}
                </section>

                <section className="rounded-xl border border-[#efd7a3] bg-[#fff8ec] p-4 min-[430px]:p-5">
                  <h2 className="text-lg font-semibold text-[#182218]">Order summary</h2>
                  <div className="mt-4 space-y-3 text-sm text-[#667063]">
                    {selectedOrderItems.map((item) => (
                      <div key={`${item.id}-${item.color || 'default'}-${item.size}`} className="flex items-start justify-between gap-3">
                        <span>
                          {item.color || 'Default'} x{item.quantity}
                          {item.size ? ` | Size ${item.size}` : ''}
                        </span>
                        <strong className="text-[#182218]">{formatCurrency(item.price * item.quantity)}</strong>
                      </div>
                    ))}
                    <div className="border-t border-[#e6dcc8] pt-3 flex items-center justify-between">
                      <span>Subtotal</span>
                      <strong className="text-[#182218]">{formatCurrency(landingTotals.subtotal)}</strong>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Delivery / commitment fee</span>
                      <strong className="text-[#182218]">
                        {selectedRoute
                          ? displayedCommitmentFee > 0
                            ? formatCurrency(displayedCommitmentFee)
                            : 'Free'
                          : 'Select route'}
                      </strong>
                    </div>
                    <div className="flex items-center justify-between text-base font-bold text-[#182218]">
                      <span>Grand total</span>
                      <span>{selectedRoute ? formatCurrency(displayedTotal) : 'Select route'}</span>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isUnavailable || isSubmittingOrder || verificationLoading || routesLoading}
                    className="mt-5 w-full rounded-xl bg-[#173822] px-5 py-4 text-base font-semibold text-white transition hover:bg-[#24552d] disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {isUnavailable
                      ? 'Out of stock'
                      : isSubmittingOrder
                      ? 'Sending order...'
                      : verificationLoading
                        ? 'Sending code...'
                        : emailVerification.isCodeSent
                          ? 'Verify & place order'
                          : 'Place order'}
                  </button>
                </section>

                <div className="grid gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      if (navigator.share) {
                        await navigator.share({
                          title: product.name,
                          text: `Check out ${product.name} - ${formatCurrency(resolvedSalePrice)}`,
                          url: typeof window !== 'undefined' ? window.location.href : buildProductLandingPath(product),
                        });
                        return;
                      }

                      await navigator.clipboard?.writeText(
                        typeof window !== 'undefined' ? window.location.href : buildProductLandingPath(product)
                      );
                      toast.success('Product link copied.');
                    }}
                    className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-xl border border-[#e6dcc8] bg-[#fffdfa] px-4 text-sm font-semibold text-[#182218] transition hover:border-[#ccb78c]"
                  >
                    <Share2 size={18} />
                    Share
                  </button>
                </div>
              </form>
            </div>
          </div>

          {shouldShowRecommendedProducts ? (
            <section
              ref={recommendedSectionRef}
              id="you-may-also-like"
              className="scroll-mt-6 space-y-5 border-t border-[#e5decc] bg-[#fbfaf5] px-3 py-8 min-[430px]:rounded-2xl min-[430px]:border min-[430px]:px-4 sm:px-6 lg:px-8"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#2b5f34]">Keep Browsing</p>
                  <h2 className="font-playfair text-3xl font-bold text-[#162218]">You may also like</h2>
                </div>
                <Link href="/products" className="text-sm font-semibold text-[#24552d] transition hover:text-[#173822]">
                  View all shoes
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
                {recommendedProducts.map((item) => {
                  const {
                    salePrice: itemSalePrice,
                    regularPrice: itemOriginalPrice,
                    hasDiscount: itemHasDiscount,
                    discount: itemDiscount,
                  } = getProductPricing(item);

                  return (
                    <Link
                      key={item.id}
                      href={buildProductLandingPath(item)}
                      className="group overflow-hidden rounded-2xl border border-[#e7e1d3] bg-white shadow-[0_16px_45px_rgba(17,17,17,0.06)] transition hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(17,17,17,0.12)]"
                    >
                      <div className="relative aspect-[4/5] overflow-hidden bg-[#f5f2e8]">
                        <Image
                          src={item.image || '/homepage banner.jpg'}
                          alt={item.name}
                          fill
                          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 260px"
                          className="object-cover transition duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="space-y-2 p-3 text-left sm:p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-700 sm:text-xs sm:tracking-[0.24em]">
                          {formatProductCategory(item) || item.department || item.category || 'Footwear'}
                        </p>
                        <h3 className="line-clamp-2 text-sm font-semibold text-gray-900 sm:text-lg">{item.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 sm:text-sm">
                          <span className="font-semibold text-[#1c6a43]">KES {itemSalePrice}</span>
                          {itemHasDiscount ? (
                            <>
                              <span className="text-gray-400 line-through">KES {itemOriginalPrice}</span>
                              <span className="rounded-full bg-[#eef7ef] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#24552d]">
                                -{itemDiscount}%
                              </span>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ) : null}

          <footer className="border-t border-[#e5decc] py-6 text-center text-xs font-semibold uppercase tracking-[0.18em] text-[#667063]">
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
              <Link href="/faq#full-terms" className="transition hover:text-[#24552d]">
                Terms &amp; Conditions
              </Link>
              <Link href="/privacy-policy" className="transition hover:text-[#24552d]">
                Privacy &amp; Cookies
              </Link>
            </div>
          </footer>
        </div>
      </div>
    </motion.div>
    </ErrorBoundary>
    </>
  );
}

ProductLandingCheckoutPage.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    details: PropTypes.string,
    category: PropTypes.string,
    subcategory: PropTypes.string,
    sizeType: PropTypes.string,
    image: PropTypes.string,
    isOutOfStock: PropTypes.bool,
    stock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sku: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    basePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    originalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sizes: PropTypes.arrayOf(PropTypes.string),
    variantInventory: PropTypes.objectOf(
      PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    ),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        image: PropTypes.string,
      })
    ),
  }),
  recommendedProducts: PropTypes.arrayOf(PropTypes.object),
  selectedColorSlug: PropTypes.string,
};
